import * as THREE from 'three/webgpu';
import type {FeatureResultMesh} from '../mesh';
import {
    disposeOwnedMaterial,
    createRotationCenterTopologyFaceMarkerMaterial,
    createRotationCenterTopologyLineMarkerMaterial,
    createRotationCenterTopologyPointMarkerMaterial,
} from '../material';
import {ROTATION_CENTER_MARKER_COLOR} from './RotationCenterDebugVisual';
import {ViewportRotationTopologyProxyKind} from './ViewportRotationFocusResolver';
import {RotationCenterSnapshotMarker} from './RotationCenterSnapshotMarker';
import type {RotationCenterTopologyHighlightTarget, RotationCenterTopologyProxy, RotationCenterTopologyHighlightSnapshot, ViewportRotationTopologyProxyKind as TopologyProxyKind} from '../../types';
export type {RotationCenterTopologyHighlightTarget} from '../../types';


/**
 * 旋转中心拓扑高亮控制器。
 *
 * 设计说明：
 * 1. 该控制器只负责旋转中心调试期间的拓扑代理视觉反馈；
 * 2. 点、线、面代理仍由 `FeatureResultMesh` 自己创建和回收，避免相机模块复制拓扑对象逻辑；
 * 3. 高亮结束时恢复原始材质与可见性，并释放本控制器创建的独占标记材质。
 */
export class RotationCenterTopologyHighlightController {
    /** 导入模型只保存最终目标的独占标记，不建立拓扑宿主。 */
    private snapshotMarker: RotationCenterSnapshotMarker | null = null;
    /** 稳定会话目标，重复刷新不创建资源。 */
    private snapshotTarget: RotationCenterTopologyHighlightTarget | null = null;
    /** 当前高亮快照。 */
    private activeHighlight: RotationCenterTopologyHighlightSnapshot | null = null;

    /**
     * 应用旋转中心拓扑高亮。
     *
     * @param target 高亮目标。
     * @returns 是否成功高亮到真实拓扑代理。
     */
    public apply(target: RotationCenterTopologyHighlightTarget | null): boolean {
        if (!target?.featureResultMesh && target?.topologySource && target.scene
            && target.topologyProxyKind && target.topologyProxyIndex !== null) {
            if (this.snapshotTarget === target) return true;
            this.clear();
            this.snapshotMarker = new RotationCenterSnapshotMarker(target.topologySource, target.topologyProxyKind, target.topologyProxyIndex);
            // 标记输入是世界空间，Scene 自身有变换时也不能再次乘一遍。
            target.scene.updateWorldMatrix(true, false);
            this.snapshotMarker.matrix.copy(target.scene.matrixWorld).invert().multiply(target.topologySource.worldMatrix);
            target.scene.add(this.snapshotMarker);
            this.snapshotMarker.updateWorldMatrix(true, true);
            this.snapshotTarget = target;
            return true;
        }
        if (!this.isValidTarget(target)) {
            this.clear();
            return false;
        }

        if (this.isHighlightingTarget(target)) {
            return true;
        }

        this.clear();

        const featureResultMesh = target.featureResultMesh;
        const topologyProxyKind = target.topologyProxyKind;
        const topologyProxyIndex = target.topologyProxyIndex;
        if (!featureResultMesh || !topologyProxyKind || topologyProxyIndex === null) {
            return false;
        }

        const retainedTopologyProxiesBeforeHighlight = this.collectCurrentTopologyProxies(featureResultMesh);
        const proxy = this.attachTopologyProxy(featureResultMesh, topologyProxyKind, topologyProxyIndex);
        if (!proxy) {
            return false;
        }

        const markerMaterial = this.createMarkerMaterial(topologyProxyKind);
        const createdByHighlight = !retainedTopologyProxiesBeforeHighlight.some((item) => item.uuid === proxy.uuid);
        this.activeHighlight = {
            featureResultMesh,
            proxy,
            topologyProxyKind,
            topologyProxyIndex,
            retainedTopologyProxiesBeforeHighlight,
            createdByHighlight,
            originalVisible: proxy.visible,
            originalRenderOrder: proxy.renderOrder,
            originalMaterial: proxy.material,
            markerMaterial,
        };

        // 旋转中心代理必须用和中心十字一致的颜色高亮，帮助判断实际旋转依据。
        proxy.material = markerMaterial

        proxy.visible = true;
        proxy.renderOrder = Math.max(proxy.renderOrder, 9001);
        proxy.updateMatrixWorld(true);
        return true;
    }

    /**
     * 清除当前旋转中心拓扑高亮。
     */
    public clear(): void {
        this.snapshotMarker?.dispose();
        this.snapshotMarker = null;
        this.snapshotTarget = null;
        if (!this.activeHighlight) {
            return;
        }

        const snapshot = this.activeHighlight;
        this.activeHighlight = null;

        if (snapshot.createdByHighlight) {
            this.releaseProxyCreatedByHighlight(snapshot);
            return;
        }

        this.restoreExistingProxy(snapshot);
    }

    /**
     * 判断当前是否已经高亮指定目标。
     *
     * @param target 高亮目标。
     * @returns 是否已经高亮该目标。
     */
    private isHighlightingTarget(target: RotationCenterTopologyHighlightTarget | null): boolean {
        if (!this.activeHighlight || !this.isValidTarget(target)) {
            return false;
        }

        return this.activeHighlight.featureResultMesh === target.featureResultMesh
            && this.activeHighlight.topologyProxyKind === target.topologyProxyKind
            && this.activeHighlight.topologyProxyIndex === target.topologyProxyIndex;
    }

    /**
     * 判断高亮目标是否完整。
     *
     * @param target 高亮目标。
     * @returns 目标是否可用于挂载真实拓扑代理。
     */
    private isValidTarget(target: RotationCenterTopologyHighlightTarget | null): target is {
        featureResultMesh: FeatureResultMesh;
        topologyProxyKind: TopologyProxyKind;
        topologyProxyIndex: number;
    } {
        return Boolean(target?.featureResultMesh)
            && Boolean(target?.topologyProxyKind)
            && Number.isInteger(target?.topologyProxyIndex)
            && Number(target?.topologyProxyIndex) >= 0;
    }

    /**
     * 收集当前结果体已经存在的拓扑代理。
     *
     * @param featureResultMesh 结果体。
     * @returns 已存在的拓扑代理集合。
     */
    private collectCurrentTopologyProxies(featureResultMesh: FeatureResultMesh): RotationCenterTopologyProxy[] {
        return [
            ...featureResultMesh.getAllFeatureFaces(),
            ...featureResultMesh.getAllFeatureLines(),
            ...featureResultMesh.getAllFeaturePoints(),
        ];
    }

    /**
     * 按目标类型挂载真实拓扑代理。
     *
     * @param featureResultMesh 结果体。
     * @param topologyProxyKind 拓扑代理类型。
     * @param topologyProxyIndex 拓扑代理索引。
     * @returns 已挂载代理；无法解析时返回 `null`。
     */
    private attachTopologyProxy(
        featureResultMesh: FeatureResultMesh,
        topologyProxyKind: TopologyProxyKind,
        topologyProxyIndex: number,
    ): RotationCenterTopologyProxy | null {
        switch (topologyProxyKind) {
            case ViewportRotationTopologyProxyKind.FeaturePoint:
                return featureResultMesh.attachFeaturePointByIndex(topologyProxyIndex);
            case ViewportRotationTopologyProxyKind.FeatureLine:
                return featureResultMesh.attachFeatureLineByIndex(topologyProxyIndex);
            case ViewportRotationTopologyProxyKind.FeatureFace:
            default:
                return featureResultMesh.attachFeatureFaceByIndex(topologyProxyIndex);
        }
    }

    /**
     * 按拓扑类型创建对应标记材质。
     *
     * @param topologyProxyKind 拓扑代理类型。
     * @returns 独占标记材质。
     */
    private createMarkerMaterial(topologyProxyKind: TopologyProxyKind): THREE.Material {
        switch (topologyProxyKind) {
            case ViewportRotationTopologyProxyKind.FeaturePoint:
                return createRotationCenterTopologyPointMarkerMaterial(ROTATION_CENTER_MARKER_COLOR);
            case ViewportRotationTopologyProxyKind.FeatureLine:
                return createRotationCenterTopologyLineMarkerMaterial(ROTATION_CENTER_MARKER_COLOR);
            case ViewportRotationTopologyProxyKind.FeatureFace:
            default:
                return createRotationCenterTopologyFaceMarkerMaterial(ROTATION_CENTER_MARKER_COLOR);
        }
    }

    /**
     * 恢复原本已存在的代理。
     *
     * @param snapshot 高亮快照。
     */
    private restoreExistingProxy(snapshot: RotationCenterTopologyHighlightSnapshot): void {
        snapshot.proxy.material = snapshot.originalMaterial
        snapshot.proxy.visible = snapshot.originalVisible;
        snapshot.proxy.renderOrder = snapshot.originalRenderOrder;
        disposeOwnedMaterial(snapshot.markerMaterial);
    }

    /**
     * 释放由本次高亮按需创建的代理。
     *
     * 设计说明：
     * 1. 新创建的代理不属于用户显式选择状态，旋转结束后应交还给 `FeatureResultMesh` 统一回收；
     * 2. 回收前保持代理材质为标记材质，避免释放点代理时误销毁点材质工厂的共享默认材质；
     * 3. 如果当前拓扑挂载模式要求保留该代理，则恢复原始材质并只释放标记材质。
     *
     * @param snapshot 高亮快照。
     */
    private releaseProxyCreatedByHighlight(snapshot: RotationCenterTopologyHighlightSnapshot): void {
        snapshot.proxy.material = snapshot.markerMaterial
        snapshot.proxy.visible = snapshot.originalVisible;
        snapshot.proxy.renderOrder = snapshot.originalRenderOrder;
        snapshot.featureResultMesh.retainTransientTopologyProxyObjects(
            snapshot.retainedTopologyProxiesBeforeHighlight,
        );

        if (snapshot.proxy.parent) {
            snapshot.proxy.material = snapshot.originalMaterial
            disposeOwnedMaterial(snapshot.markerMaterial);
            return;
        }

        if (snapshot.topologyProxyKind !== ViewportRotationTopologyProxyKind.FeaturePoint) {
            disposeOwnedMaterial(snapshot.originalMaterial);
        }
    }
}

